import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Users, GraduationCap, Briefcase, HeartHandshake, ShieldCheck, RefreshCw, Calendar, Crown, CreditCard, HardDrive, Loader2, Baby, Pencil } from "lucide-react";
import { fmtBytes } from "@/components/docs/docUtils";
import { EditClientDialog } from "@/components/platform/EditClientDialog";
import { OwnerSubscriptionCard } from "@/components/platform/OwnerSubscriptionCard";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const ROLE_LABELS = { admin: "Admins", corporate_admin: "Corporate admins", officer: "Officers", staff: "Staff", teacher: "Teachers", driver: "Drivers" };

export default function PlatformCompanyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/platform/company/${id}/detail`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  const resetMfa = async (a) => {
    if (!window.confirm(`Reset ${a.name}'s authenticator app? They'll get sign-in codes by email until they set it up again.`)) return;
    try {
      await api.post(`/platform/users/${a.id}/mfa/reset`);
      toast.success("Authenticator reset");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (loading && !data) {
    return <main className="px-6 md:px-10 py-8 text-slate-500" data-testid="company-detail-loading">Loading…</main>;
  }
  if (!data) {
    return <main className="px-6 md:px-10 py-8 text-slate-500">Company not found.</main>;
  }

  const c = data.company;
  const isSchool = (c.type || "business") === "school";
  const isCare = c.type === "care";
  const isDaycare = c.type === "daycare";
  const status = c.deactivated
    ? { label: "Deactivated", cls: "bg-slate-200 text-slate-700" }
    : c.paid_until && new Date(c.paid_until) > new Date()
    ? { label: "Active", cls: "bg-emerald-100 text-emerald-800" }
    : c.trial_ends_at && new Date(c.trial_ends_at) > new Date()
    ? { label: "Trial", cls: "bg-amber-100 text-amber-800" }
    : { label: "Expired", cls: "bg-red-100 text-red-800" };

  return (
    <main className="px-6 md:px-10 py-8" data-testid="platform-company-detail">
      <Link to="/platform/companies" data-testid="company-detail-back" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> All companies
      </Link>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5" data-testid="company-detail-name">
            {isSchool ? <GraduationCap className="h-6 w-6 text-blue-600" /> : isCare ? <HeartHandshake className="h-6 w-6 text-emerald-600" /> : isDaycare ? <Baby className="h-6 w-6 text-pink-600" /> : <Briefcase className="h-6 w-6 text-slate-600" />}
            {c.name}
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${status.cls}`} data-testid="company-detail-status">
              {status.label}
            </span>
          </h1>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>{isSchool ? "School" : isCare ? "Personal Care Home / Adult Care" : isDaycare ? "Daycare / Pre-K" : "Business/Office"}</span>
            <span className="font-mono">{c.id.slice(0, 8)}…</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Joined {new Date(c.created_at).toLocaleDateString()}</span>
            {c.paid_until && <span>Paid until {new Date(c.paid_until).toLocaleDateString()}</span>}
            {data.corporate_name && (
              <span className="inline-flex items-center gap-1 text-amber-700"><Crown className="h-3 w-3" /> {data.corporate_name}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
        <button
          onClick={() => setEditOpen(true)}
          data-testid="company-edit-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 transition"
        >
          <Pencil className="h-4 w-4" /> Edit client
        </button>
        <button
          onClick={load}
          data-testid="platform-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        </div>
      </header>

      {editOpen && (
        <EditClientDialog open onClose={() => setEditOpen(false)} company={c} building={data.building} onSaved={load} />
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="company-detail-counts">
        <CountCard Icon={ShieldCheck} label="Admins" value={data.admins.length} />
        <CountCard Icon={Users} label="Employees" value={data.counts.employees} />
        {isSchool && <CountCard Icon={GraduationCap} label="Students" value={data.counts.students} accent="blue" />}
        {isDaycare && <CountCard Icon={Baby} label="Children" value={data.counts.students} accent="blue" />}
        {isCare && <CountCard Icon={HeartHandshake} label="Clients" value={data.counts.clients ?? 0} accent="blue" />}
        <CountCard Icon={CreditCard} label="Payments" value={data.payments.length} />
      </section>

      {data.storage && <StorageCard companyId={c.id} storage={data.storage} onSaved={load} />}
      {!data.corporate_name && <OwnerSubscriptionCard targetType="company" targetId={c.id} subscription={data.subscription} onChanged={load} />}
      <BaaCard companyId={c.id} compliance={data.compliance} onSaved={load} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="company-detail-admins">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="font-display text-base font-semibold text-slate-900">Admins</div>
            <div className="text-xs text-slate-500">People who can manage this company.</div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5">Name</th>
                <th className="px-5 py-2.5">Email</th>
                <th className="px-5 py-2.5">Role</th>
                <th className="px-5 py-2.5">2FA</th>
              </tr>
            </thead>
            <tbody>
              {data.admins.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-500">No admins found.</td></tr>
              )}
              {data.admins.map((a) => (
                <tr key={a.id} className="border-t border-slate-100" data-testid={`company-admin-${a.id}`}>
                  <td className="px-5 py-2.5 font-medium text-slate-900">{a.name}</td>
                  <td className="px-5 py-2.5 text-slate-700">{a.email}</td>
                  <td className="px-5 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
                      a.role === "corporate_admin" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {a.role === "corporate_admin" ? "Corporate" : "Admin"}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-xs" data-testid={`company-admin-mfa-${a.id}`}>
                    {a.mfa_enabled ? (
                      <span className="inline-flex items-center gap-2"><span className="text-emerald-700 font-semibold">Authenticator</span>
                        <button type="button" onClick={() => resetMfa(a)} className="text-slate-500 hover:text-rose-700 hover:underline" data-testid={`company-admin-mfa-reset-${a.id}`}>Reset</button></span>
                    ) : <span className="text-slate-400">Email code</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500 flex gap-3 flex-wrap" data-testid="company-detail-roles">
            {Object.entries(data.counts.by_role).map(([role, n]) => (
              <span key={role}>{ROLE_LABELS[role] || role}: <strong className="text-slate-700">{n}</strong></span>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="company-detail-payments">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="font-display text-base font-semibold text-slate-900">Payment history</div>
            <div className="text-xs text-slate-500">Stripe checkouts for this company (incl. via its corporate).</div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="px-5 py-2.5">Date</th>
                  <th className="px-5 py-2.5">Plan</th>
                  <th className="px-5 py-2.5 text-right">Amount</th>
                  <th className="px-5 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">No payments yet.</td></tr>
                )}
                {data.payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`company-payment-${p.id}`}>
                    <td className="px-5 py-2.5 text-slate-700 whitespace-nowrap">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                    <td className="px-5 py-2.5">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-700">
                        {p.plan === "annual" ? "Annual" : "Monthly"}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-right font-semibold text-slate-900">${p.amount.toFixed(2)}</td>
                    <td className="px-5 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
                        p.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                      }`}>
                        {p.payment_status === "paid" ? "Paid" : p.payment_status || "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
}

function StorageCard({ companyId, storage, onSaved }) {
  const [gb, setGb] = useState(String(Math.round((storage.limit_bytes / 1024 ** 3) * 100) / 100));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setGb(String(Math.round((storage.limit_bytes / 1024 ** 3) * 100) / 100)); }, [storage.limit_bytes]);
  const pct = storage.limit_bytes ? Math.min(100, (storage.used_bytes / storage.limit_bytes) * 100) : 0;
  const save = async (limit_gb) => {
    setBusy(true);
    try { await api.put(`/platform/company/${companyId}/storage`, { limit_gb }); toast.success(limit_gb ? `Storage limit set to ${limit_gb} GB` : "Back to the platform default"); onSaved(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update storage limit"); }
    finally { setBusy(false); }
  };
  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="company-detail-storage">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[260px] flex-1">
          <div className="font-display text-base font-semibold text-slate-900 inline-flex items-center gap-2"><HardDrive className="h-4 w-4 text-slate-500" /> File storage</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900" data-testid="company-storage-text">{fmtBytes(storage.used_bytes)} <span className="text-base font-medium text-slate-400">/ {fmtBytes(storage.limit_bytes)}</span></div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${pct >= 95 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${Math.max(pct, storage.used_bytes ? 1 : 0)}%` }} /></div>
          <div className="mt-1 text-xs text-slate-500">{storage.file_count} file{storage.file_count === 1 ? "" : "s"} · {pct.toFixed(pct < 10 && pct > 0 ? 1 : 0)}% used · {storage.custom ? "custom limit" : "platform default"}{storage.addon_bytes ? ` + ${fmtBytes(storage.addon_bytes)} purchased` : ""}</div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); const v = Number(gb); if (!v || v <= 0) { toast.error("Enter a limit in GB"); return; } save(v); }} className="flex items-end gap-2">
          <label className="block">
            <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Limit (GB)</span>
            <input type="number" min="0.1" step="any" value={gb} onChange={(e) => setGb(e.target.value)} data-testid="company-storage-input"
              className="h-10 w-32 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          </label>
          <button type="submit" disabled={busy} data-testid="company-storage-save" className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-slate-900 px-3 text-sm font-medium text-white disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          {storage.custom && <button type="button" disabled={busy} onClick={() => save(null)} data-testid="company-storage-reset" className="h-10 rounded-lg border border-slate-200 px-3 text-sm text-slate-700 hover:bg-slate-50">Use default</button>}
        </form>
      </div>
    </section>
  );
}

function CountCard({ Icon, label, value, accent }) {
  const ring = { blue: "ring-blue-200 bg-blue-50" }[accent] || "ring-slate-200 bg-white";
  return (
    <div className={`rounded-xl border border-slate-200 p-5 ring-1 ${ring}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">{value}</div>
    </div>
  );
}


function BaaCard({ companyId, compliance, onSaved }) {
  const [busy, setBusy] = useState(false);
  const signed = compliance?.baa_signed_at;
  const toggle = async () => {
    setBusy(true);
    try { await api.put(`/platform/company/${companyId}/baa`, { signed: !signed }); toast.success(signed ? "BAA marked as not signed" : "BAA marked as signed"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-slate-200 bg-white px-5 py-4 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="company-baa-card">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary inline-flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
        <div>
          <div className="font-display text-base font-semibold text-slate-900">HIPAA Business Associate Agreement</div>
          <div className="text-xs text-slate-500" data-testid="company-baa-status">{signed ? `Signed ${new Date(signed).toLocaleDateString()}${compliance?.baa_signed_by ? ` · recorded by ${compliance.baa_signed_by}` : ""}` : "Not signed — shows as a to-do on the customer's Compliance checklist."}</div>
        </div>
      </div>
      <button onClick={toggle} disabled={busy} data-testid="company-baa-toggle"
              className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-semibold disabled:opacity-60 ${signed ? "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" : "bg-primary text-white"}`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {signed ? "Mark as not signed" : "Mark BAA signed"}
      </button>
    </section>
  );
}
